/**
 * Per-Topic navigation store.
 *
 * This deliberately does not carry a site-wide topic catalogue. A Topic is
 * fetched only when it is opened or needed by Chapter/player navigation,
 * through the bounded `/api/topics/:slug/navigation` projection.
 */

import { parseStoredTopicSource } from '../../domain/topicSourcePolicy';

export interface TopicChapter {
	id: string;
	index: number;
	title: string;
	subtitle?: string;
	summary?: string;
	duration: string;
	previewBody?: string;
	hasAudio?: boolean;
	hasVideo?: boolean;
}

export interface TopicRecord {
	id: string;
	slug: string;
	title: string;
	subtitle: string;
	category: string;
	source?: import('../../domain/types/contentCommonTypes').TopicSource;
	classification?: import('../../domain/topicClassification').TopicClassification | null;
	coverBadge?: 'github' | 'new' | 'trending';
	coverImageUrl?: string;
	coverIcon: string;
	coverGradient: string;
	previewChapters: TopicChapter[];
	lockedChapters: TopicChapter[];
	isPrivate: boolean;
	audioMinutes: number;
	isNewThisWeek: boolean;
	isRecommended: boolean;
	subscribers?: number;
	openCount?: number;
	favoriteCount?: number;
	dynamicScore?: number;
	createdAt?: string;
	engagement?: {
		chapters?: number;
		mediaMinutes?: number;
		opened?: number;
		saved?: number;
	};
	keywords?: Array<{ keyword: string; coefficient: number }>;
}

type NavigationTopic = Omit<TopicRecord, 'isPrivate' | 'coverIcon' | 'coverGradient'> & {
	isPublic?: number;
	source?: string;
};

type NavigationResponse = { topic?: NavigationTopic | null };
type StoredTopic = { record: TopicRecord; completeNavigation: boolean };

let cache = new Map<string, StoredTopic>();
let inflight = new Map<string, Promise<TopicRecord>>();
let invalidationEpoch = 0;

let fetchTopic: (slugOrId: string) => Promise<unknown> = defaultFetchTopic;

async function defaultFetchTopic(slugOrId: string): Promise<unknown> {
	const response = await fetch(`/api/topics/${encodeURIComponent(slugOrId)}/navigation`, { credentials: 'include' });
	if (!response.ok) throw new Error(`topic-navigation ${response.status}`);
	return response.json();
}

/** Configure the one-Topic navigation reader used by the SPA API client. */
export function configureTopicFetcher(fetcher: (slugOrId: string) => Promise<unknown>): void {
	fetchTopic = fetcher;
}

/**
 * Seeds immediate SSR presentation only. PageModel directories omit inactive
 * or unrenderable rows by design, so `ensureTopic()` still reads the complete
 * navigation projection before it drives previous/next Chapter controls.
 */
export function seedFromPageModel(
	topic: { id?: string; slug: string; title: string; subtitle: string; category: string; classification?: TopicRecord['classification']; coverBadge?: TopicRecord['coverBadge']; coverImageUrl?: string; source?: import('../../domain/types/contentTypes').TopicSource },
	chapters: Array<{ index: number; title: string; duration: string; hasAudio?: boolean; hasVideo?: boolean; subtitle?: string; summary?: string }>,
): void {
	if (!topic?.slug || !topic?.title) return;
	const existing = readStoredTopic(topic.slug) ?? (topic.id ? readStoredTopic(topic.id) : undefined);
	if (existing) {
		if (topic.id) cache.set(topic.id, existing);
		return;
	}
	const previewChapters = chapters.map((chapter) => ({
		id: `sr-${topic.slug}-${chapter.index}`,
		index: chapter.index,
		title: chapter.title,
		duration: chapter.duration ?? '',
		hasAudio: chapter.hasAudio,
		hasVideo: chapter.hasVideo,
		...(chapter.subtitle ? { subtitle: chapter.subtitle } : {}),
		...(chapter.summary ? { summary: chapter.summary } : {}),
	}));
	storeTopic({
		id: topic.id || `sr-${topic.slug}`,
		slug: topic.slug,
		title: topic.title,
		subtitle: topic.subtitle ?? '',
		category: topic.category ?? '',
		classification: topic.classification ?? null,
		coverBadge: topic.coverBadge,
		coverImageUrl: topic.coverImageUrl,
		coverIcon: '📘',
		coverGradient: 'grad-ai',
		previewChapters,
		lockedChapters: [],
		source: topic.source,
		isPrivate: topic.source === 'private_ai',
		audioMinutes: 0,
		isNewThisWeek: false,
		isRecommended: false,
	}, false);
}

/**
 * Ensures one Topic's complete navigation DTO is present. Requests are
 * deduplicated per Topic key for the current viewer/invalidation epoch; an
 * identity reset makes old responses fail instead of repainting a new view.
 */
export function ensureTopic(slugOrId: string): Promise<TopicRecord> {
	const cached = readStoredTopic(slugOrId);
	if (cached?.completeNavigation) return Promise.resolve(cached.record);
	// PageModel seeding already knows both aliases. Use its canonical slug so a
	// simultaneous Chapter route (id) and Topic route (slug) share one read.
	const requestKey = cached?.record.slug ?? slugOrId;
	const pending = inflight.get(requestKey);
	if (pending) return pending;

	const requestEpoch = invalidationEpoch;
	let request: Promise<TopicRecord>;
	request = fetchTopic(slugOrId).then((response) => {
		if (invalidationEpoch !== requestEpoch) throw new Error('topic_navigation_invalidated');
		const record = mapNavigationTopic(response);
		storeTopic(record, true);
		return record;
	}).finally(() => {
		if (invalidationEpoch === requestEpoch && inflight.get(requestKey) === request) inflight.delete(requestKey);
	});
	inflight.set(requestKey, request);
	return request;
}

export function getTopic(slugOrId: string): TopicRecord | undefined {
	return readStoredTopic(slugOrId)?.record;
}

export function invalidateTopicIndex(): void {
	invalidationEpoch += 1;
	cache = new Map();
	inflight = new Map();
}

function readStoredTopic(slugOrId: string): StoredTopic | undefined {
	return cache.get(slugOrId);
}

function storeTopic(record: TopicRecord, completeNavigation: boolean): void {
	const stored = { record, completeNavigation };
	cache.set(record.slug, stored);
	if (record.id) cache.set(record.id, stored);
}

function mapNavigationTopic(value: unknown): TopicRecord {
	const topic = (value as NavigationResponse | null)?.topic;
	if (!topic || typeof topic.id !== 'string' || typeof topic.slug !== 'string' || typeof topic.title !== 'string') {
		throw new Error('invalid_topic_navigation_response');
	}
	const source = parseStoredTopicSource(topic.source) ?? undefined;
	return {
		id: topic.id,
		slug: topic.slug,
		title: topic.title,
		subtitle: topic.subtitle ?? '',
		category: topic.category ?? '',
		classification: topic.classification ?? null,
		coverBadge: topic.coverBadge,
		coverImageUrl: topic.coverImageUrl,
		coverIcon: '📘',
		coverGradient: 'grad-ai',
		previewChapters: mapChapters(topic.previewChapters),
		lockedChapters: mapChapters(topic.lockedChapters),
		source,
		isPrivate: source === 'private_ai' || topic.isPublic === 0,
		audioMinutes: topic.audioMinutes ?? 0,
		isNewThisWeek: topic.isNewThisWeek === true,
		isRecommended: topic.isRecommended === true,
		subscribers: topic.subscribers ?? 0,
		openCount: topic.openCount ?? 0,
		favoriteCount: topic.favoriteCount ?? 0,
		dynamicScore: topic.dynamicScore ?? 0,
		...(topic.engagement ? { engagement: topic.engagement } : {}),
		...(Array.isArray(topic.keywords) ? { keywords: topic.keywords } : {}),
		...(topic.createdAt ? { createdAt: topic.createdAt } : {}),
	};
}

function mapChapters(chapters: unknown): TopicChapter[] {
	if (!Array.isArray(chapters)) return [];
	return chapters.flatMap((chapter) => {
		if (!chapter || typeof chapter !== 'object') return [];
		const candidate = chapter as Partial<TopicChapter>;
		if (typeof candidate.id !== 'string' || !Number.isInteger(candidate.index) || typeof candidate.title !== 'string' || typeof candidate.duration !== 'string') return [];
		const { id, index, title, duration } = candidate as Required<Pick<TopicChapter, 'id' | 'index' | 'title' | 'duration'>>;
		return [{
			id,
			index,
			title,
			duration,
			...(typeof candidate.subtitle === 'string' ? { subtitle: candidate.subtitle } : {}),
			...(typeof candidate.summary === 'string' ? { summary: candidate.summary } : {}),
			...(typeof candidate.previewBody === 'string' ? { previewBody: candidate.previewBody } : {}),
			...(typeof candidate.hasAudio === 'boolean' ? { hasAudio: candidate.hasAudio } : {}),
			...(typeof candidate.hasVideo === 'boolean' ? { hasVideo: candidate.hasVideo } : {}),
		}];
	});
}
